import { Tooltip } from '@odo/shared-ui';
import type { CaseTag } from './caseTags';

type TagSize = 'sm' | 'md';

const PILL_BASE_CLASS: Record<TagSize, string> = {
    sm: 'flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium tracking-wide ring-1 ring-inset',
    md: 'flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium tracking-wide ring-1 ring-inset'
};
const DOT_BASE_CLASS: Record<TagSize, string> = {
    sm: 'w-1.5 h-1.5 rounded-full',
    md: 'w-2 h-2 rounded-full'
};
const OVERFLOW_CLASS: Record<TagSize, string> = {
    sm: 'flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium tracking-wide ring-1 ring-inset bg-gray-50 text-gray-600 ring-gray-500/20',
    md: 'flex-shrink-0 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium tracking-wide ring-1 ring-inset bg-gray-50 text-gray-600 ring-gray-500/20'
};

const DEFAULT_MAX_VISIBLE = 4;

/** Single case-tag pill with its tooltip. Shared across row, task surface, and modal headers. */
export function CaseTagPill({ tag, size = 'sm' }: { tag: CaseTag; size?: TagSize }) {
    return (
        <Tooltip content={tag.tooltip}>
            <span className={`${PILL_BASE_CLASS[size]} ${tag.className}`}>
                <span
                    className={`${DOT_BASE_CLASS[size]} ${tag.dotClassName}`}
                    aria-hidden="true"
                />
                {tag.label}
            </span>
        </Tooltip>
    );
}

interface CaseTagListProps {
    tags: CaseTag[];
    maxVisible?: number;
    size?: TagSize;
}

// Renders up to maxVisible tags inline; anything beyond collapses into a
// "+N" chip whose tooltip lists the overflowed labels.
export function CaseTagList({
    tags,
    maxVisible = DEFAULT_MAX_VISIBLE,
    size = 'sm'
}: CaseTagListProps) {
    const visible = tags.slice(0, maxVisible);
    const rest = tags.slice(maxVisible);
    return (
        <>
            {visible.map((tag) => (
                <CaseTagPill key={tag.key} tag={tag} size={size} />
            ))}
            {rest.length > 0 && (
                <Tooltip
                    content={
                        <span className="flex flex-col gap-0.5">
                            {rest.map((t) => (
                                <span key={t.key}>{t.label}</span>
                            ))}
                        </span>
                    }
                >
                    <span className={OVERFLOW_CLASS[size]}>+{rest.length}</span>
                </Tooltip>
            )}
        </>
    );
}
